import { useMemo, useState } from 'react'
import { collection, query, where } from 'firebase/firestore'
import { db } from '../lib/firebase'
import { useBatchedSubscriptions } from './useBatchedSubscriptions'
import type { PartialRange, SetInterest } from '../types'

const EMPTY_RECORD: Record<string, string[]> = {}
const EMPTY_BUCKETED = { interested: EMPTY_RECORD, going: EMPTY_RECORD }
const EMPTY_RANGES: SetRangesByUser = {}

// Hands back the array it last returned for a key while its members are unchanged.
class ArrayReuse {
  private last = new Map<string, string[]>()
  get(key: string, ids: Set<string>): string[] {
    const before = this.last.get(key)
    if (before && before.length === ids.size && before.every(id => ids.has(id))) return before
    const next = [...ids]
    this.last.set(key, next)
    return next
  }
}

export interface BucketedUserIds {
  interested: Record<string, string[]>
  going: Record<string, string[]>
}

/** setId → userId → the slice of that set the user will actually be there for. */
export type SetRangesByUser = Record<string, Record<string, PartialRange>>

// Crew artist hearts on an event live in useFriendEventArtistInterests, so only
// the surfaces showing artist facepiles pay for them.
export function useGroupInterests(memberIds: string[], eventId: string) {
  const sets = useBatchedSubscriptions<SetInterest>(
    memberIds,
    batch =>
      query(
        collection(db, 'setInterests'),
        where('userId', 'in', batch),
        where('eventId', '==', eventId),
      ),
    d => d.data() as SetInterest,
    { queryKey: eventId },
  )

  // Deduped per set: a user can have more than one interest doc for the same
  // set, but each should appear once in the overlay. A set whose people didn't
  // change keeps its previous array, so a snapshot only re-renders the
  // (memoized) blocks it touched instead of every block with crew picks.
  const [reuse] = useState(() => new ArrayReuse())
  const setUserIds = useMemo<BucketedUserIds>(() => {
    if (sets.rows.length === 0) return EMPTY_BUCKETED
    const interestedSets: Record<string, Set<string>> = {}
    const goingSets: Record<string, Set<string>> = {}
    sets.rows.forEach(i => {
      const bucket = i.status === 'going' ? goingSets : interestedSets
      ;(bucket[i.setId] ??= new Set()).add(i.userId)
    })
    const toArrays = (b: Record<string, Set<string>>, bucket: string): Record<string, string[]> => {
      const out: Record<string, string[]> = {}
      for (const [setId, ids] of Object.entries(b)) out[setId] = reuse.get(`${bucket}:${setId}`, ids)
      return out
    }
    return { interested: toArrays(interestedSets, 'interested'), going: toArrays(goingSets, 'going') }
  }, [sets.rows, reuse])

  // Kept parallel to `setUserIds` rather than folded into it: the buckets are
  // consumed as plain id arrays in a dozen places, and only a handful of
  // surfaces care about the ranges.
  const setRanges = useMemo<SetRangesByUser>(() => {
    const ranges: SetRangesByUser = {}
    sets.rows.forEach(i => {
      if (!i.partialRange) return
      ;(ranges[i.setId] ??= {})[i.userId] = i.partialRange
    })
    return Object.keys(ranges).length === 0 ? EMPTY_RANGES : ranges
  }, [sets.rows])

  return {
    setUserIds,
    setRanges,
    loading: sets.loading,
    error: sets.error,
  }
}
